import React, { useEffect, useState } from "react";
import { NavLink, Routes, Route, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, Kanban, Truck, Users, Receipt, MessageSquare, LogOut, Search, Building2, UserCog, MapPin,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { LOGO_URL } from "@/lib/constants";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import DispatchBoard from "@/pages/admin/DispatchBoard";
import JobsPage from "@/pages/admin/JobsPage";
import JobDetail from "@/pages/admin/JobDetail";
import CustomersPage from "@/pages/admin/CustomersPage";
import CarriersPage from "@/pages/admin/CarriersPage";
import InvoicesPage from "@/pages/admin/InvoicesPage";
import MessagesPage from "@/pages/admin/MessagesPage";
import UsersPage from "@/pages/admin/UsersPage";
import AddressDirectory from "@/pages/admin/AddressDirectory";
import CarrierPayPage from "@/pages/admin/CarrierPayPage";

const NavItem = ({ to, icon: Icon, children, testId, end }) => (
  <NavLink
    to={to}
    end={end}
    data-testid={testId}
    className={({ isActive }) =>
      `flex items-center gap-3 px-4 py-2.5 text-sm border-l-2 ${
        isActive
          ? "bg-zinc-900 text-white border-[#0056B3]"
          : "text-zinc-400 border-transparent hover:text-white hover:bg-zinc-900"
      }`
    }
  >
    <Icon className="h-4 w-4 stroke-[1.5]" /> {children}
  </NavLink>
);

const Sidebar = ({ user, onLogout, unreadMsgs }) => (
  <aside className="w-64 bg-zinc-950 text-zinc-100 flex flex-col border-r border-zinc-900 min-h-screen fixed">
    <Link to="/admin" className="flex items-center gap-3 px-5 py-5 border-b border-zinc-900">
      <img src={LOGO_URL} alt="Apex" className="h-9 w-9 bg-white p-1" />
      <div>
        <div className="font-bold tracking-tight text-white leading-none">APEX</div>
        <div className="mono-label !text-zinc-500 !text-[10px]">TMS CONTROL ROOM</div>
      </div>
    </Link>
    <nav className="flex-1 py-4 space-y-0.5">
      <NavItem to="/admin" end icon={LayoutDashboard} testId="nav-dashboard">Dashboard</NavItem>
      <NavItem to="/admin/dispatch" icon={Kanban} testId="nav-dispatch">Dispatch board</NavItem>
      <NavItem to="/admin/jobs" icon={Truck} testId="nav-jobs">Jobs</NavItem>
      <NavItem to="/admin/customers" icon={Users} testId="nav-customers">Portal customers</NavItem>
      <NavItem to="/admin/directory" icon={MapPin} testId="nav-directory">Customers &amp; Consignees</NavItem>
      <NavItem to="/admin/carriers" icon={Building2} testId="nav-carriers">Carriers</NavItem>
      <NavItem to="/admin/carrier-pay" icon={UserCog} testId="nav-carrier-pay">Carrier Pay</NavItem>
      <NavItem to="/admin/invoices" icon={Receipt} testId="nav-invoices">Invoices</NavItem>
      <NavItem to="/admin/messages" icon={MessageSquare} testId="nav-messages">
        Messages
        {unreadMsgs > 0 && (
          <span className="ml-auto bg-[#0056B3] text-white font-mono text-[10px] px-1.5 py-0.5">
            {unreadMsgs}
          </span>
        )}
      </NavItem>
      {user.role === "admin" && (
        <NavItem to="/admin/users" icon={UserCog} testId="nav-users">User management</NavItem>
      )}
    </nav>
    <div className="border-t border-zinc-900 px-5 py-4">
      <div className="text-sm font-medium">{user.name}</div>
      <div className="mono-label !text-zinc-500 !text-[10px]">{user.role} &middot; {user.email}</div>
      <Button variant="outline" onClick={onLogout} data-testid="admin-logout"
        className="sharp mt-3 w-full bg-transparent text-zinc-100 border-zinc-800 hover:bg-zinc-900 hover:text-white">
        <LogOut className="h-4 w-4 mr-2" /> Sign out
      </Button>
    </div>
  </aside>
);

const TopBar = ({ title }) => (
  <header className="sticky top-0 z-10 bg-white border-b border-zinc-200 h-16 flex items-center justify-between px-8">
    <h1 className="text-xl font-bold tracking-tight">{title}</h1>
    <div className="flex items-center gap-3">
      <div className="relative">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input placeholder="Search jobs, POs..." className="sharp apex-input h-9 pl-9 w-72" data-testid="top-search" />
      </div>
    </div>
  </header>
);

const Shell = ({ children, title, user, onLogout, unreadMsgs }) => (
  <div className="min-h-screen bg-zinc-50 flex">
    <Sidebar user={user} onLogout={onLogout} unreadMsgs={unreadMsgs} />
    <div className="flex-1 ml-64">
      <TopBar title={title} />
      <main className="p-8">{children}</main>
    </div>
  </div>
);

const routeTitles = {
  "/admin": "Dashboard",
  "/admin/dispatch": "Dispatch board",
  "/admin/jobs": "Jobs",
  "/admin/customers": "Portal customers",
  "/admin/directory": "Customers & Consignees",
  "/admin/carriers": "Carriers",
  "/admin/invoices": "Invoices",
  "/admin/carrier-pay": "Carrier Pay",
  "/admin/messages": "Messages",
  "/admin/users": "User management",
};

export default function AdminShell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [unread, setUnread] = useState(0);

  const loadUnread = async () => {
    try {
      const { data } = await api.get("/stats/overview");
      setUnread(data.unread_messages || 0);
    } catch {}
  };
  useEffect(() => {
    loadUnread();
    const t = setInterval(loadUnread, 30000);
    return () => clearInterval(t);
  }, []);

  const doLogout = async () => { await logout(); nav("/"); };

  const title = (() => {
    const path = window.location.pathname;
    if (path.startsWith("/admin/jobs/")) return "Job detail";
    return routeTitles[path] || "Dashboard";
  })();

  return (
    <Shell title={title} user={user} onLogout={doLogout} unreadMsgs={unread}>
      <Routes>
        <Route index element={<AdminDashboard />} />
        <Route path="dispatch" element={<DispatchBoard />} />
        <Route path="jobs" element={<JobsPage />} />
        <Route path="jobs/:id" element={<JobDetail onChange={loadUnread} />} />
        <Route path="customers" element={<CustomersPage />} />
        <Route path="directory" element={<AddressDirectory />} />
        <Route path="carriers" element={<CarriersPage />} />
        <Route path="carrier-pay" element={<CarrierPayPage />} />
        <Route path="invoices" element={<InvoicesPage />} />
        <Route path="messages" element={<MessagesPage onRead={loadUnread} />} />
        {user.role === "admin" && (
          <Route path="users" element={<UsersPage />} />
        )}
      </Routes>
    </Shell>
  );
}
